import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Sparkles, ChevronRight, Bot, Users, Activity, FileText, Calendar, MessagesSquare, Play, ExternalLink } from "lucide-react";
import RudrasLogo from "../components/RudrasLogo";
import CinematicWalkthrough from "../components/CinematicWalkthrough";
import ShowcaseInboundLeadForm from "../components/ShowcaseInboundLeadForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { "AI-handled conversations": MessagesSquare, "Qualified leads": Users, "Avg. conversation depth": Activity, "Knowledge base": FileText, "Showcase conversations": Sparkles };

function FunnelBar({ row, max }) {
  const widthPct = max > 0 ? Math.max(4, Math.round((row.count / max) * 100)) : 4;
  const color = {
    High: "bg-emerald-400",
    Medium: "bg-amber-400",
    Marketing: "bg-sky-400",
    Lost: "bg-rose-400",
    Junk: "bg-stone-400",
  }[row.status] || "bg-muted-foreground";
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <div className="font-medium text-white">{row.status}</div>
        <div className="text-white/70">{row.label} · <span className="font-semibold text-white">{row.count}</span></div>
      </div>
      <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${widthPct}%` }}/>
      </div>
    </div>
  );
}

function HeatCell({ count, max }) {
  const intensity = max > 0 ? count / max : 0;
  let bg;
  if (count === 0) bg = "bg-white/5";
  else if (intensity < 0.34) bg = "bg-emerald-300/40";
  else if (intensity < 0.67) bg = "bg-emerald-400/70";
  else bg = "bg-emerald-400";
  return (
    <div className={`h-8 sm:h-9 ${bg} rounded grid place-items-center text-[11px] font-medium ${intensity >= 0.67 ? "text-emerald-950" : "text-white/80"}`}>
      {count > 0 ? count : ""}
    </div>
  );
}

function ScoreGauge({ score, grade }) {
  const arc = Math.max(0, Math.min(100, score));
  const radius = 80;
  const circ = Math.PI * radius;
  const offset = circ - (circ * arc) / 100;
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 110" className="w-full max-w-[240px]" aria-hidden>
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="14" strokeLinecap="round"/>
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="url(#pub-gauge-grad)" strokeWidth="14" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 1s ease-out" }}/>
        <defs>
          <linearGradient id="pub-gauge-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#34d399"/>
            <stop offset="100%" stopColor="#a7f3d0"/>
          </linearGradient>
        </defs>
      </svg>
      <div className="-mt-6 text-center">
        <div className="font-display text-5xl font-semibold tabular-nums text-white">{arc}</div>
        <div className="text-sm font-medium uppercase tracking-wider text-emerald-300">{grade}</div>
      </div>
    </div>
  );
}

export default function ShowcasePage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const skipMode = params.get("mode") === "interactive";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const [mode, setMode] = useState(skipMode ? "interactive" : "walkthrough");

  // Source tracking — utm_source > ?source > iframe-detect > showcase
  const inboundSource = (() => {
    const sourceParam = (params.get("utm_source") || params.get("source") || "").toLowerCase();
    if (sourceParam) {
      // Map common LinkedIn/QR/Twitter inputs to allow-listed values
      if (sourceParam.includes("linkedin")) return "linkedin";
      if (sourceParam.includes("twitter") || sourceParam.includes("x.com")) return "twitter";
      if (sourceParam.includes("qr")) return "qr";
      if (sourceParam.includes("conf")) return "conference";
      if (sourceParam.includes("deck")) return "deck";
      if (sourceParam.includes("email")) return "email";
      if (sourceParam.includes("iframe") || sourceParam.includes("embed")) return "iframe";
      if (sourceParam.includes("direct")) return "direct";
      return "other";
    }
    if (embed) return "iframe";
    try { if (window.self !== window.top) return "iframe"; } catch (_e) { return "iframe"; }
    return "showcase";
  })();

  useEffect(() => {
    axios.get(`${API}/public/showcase/${slug}`)
      .then((r) => {
        setData(r.data);
        if (!skipMode) {
          // Brief settling pause then auto-open the walkthrough
          setTimeout(() => setWalkthroughOpen(true), 500);
        }
      })
      .catch((e) => setError(e?.response?.status === 404 ? "This showcase isn't available." : "Couldn't load the showcase."));
    document.title = "Rudras-AI · Live Demo";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  function handleSkipToInteractive() {
    setWalkthroughOpen(false);
    setMode("interactive");
  }
  function replayWalkthrough() {
    setMode("walkthrough");
    setWalkthroughOpen(true);
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-950 text-white p-8">
        <div className="text-center max-w-md" data-testid="showcase-error">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-xs uppercase tracking-[0.3em] text-white/70 mb-4">
            <Sparkles className="w-3.5 h-3.5"/> Rudras-AI
          </div>
          <h1 className="font-display text-3xl font-semibold mb-2">{error}</h1>
          <p className="text-white/60 mb-6">Try the live marketing site for the latest demos.</p>
          <Link to="/"><Button>Back to home</Button></Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-br from-[#0b3a31] to-[#06241e] text-white">
        <div className="text-center" data-testid="showcase-loading">
          <Sparkles className="w-6 h-6 mx-auto mb-3 animate-pulse text-emerald-300"/>
          <p className="text-white/60 text-sm uppercase tracking-[0.3em]">Loading the experience…</p>
        </div>
      </div>
    );
  }

  const { lead_funnel, lead_funnel_total, ai_engagement, heatmap, executive_summary, company, chatbot_url } = data;
  const maxLead = Math.max(1, ...lead_funnel.map((r) => r.count));
  const maxHeat = Math.max(1, ...heatmap.flatMap((row) => row.cells.map((c) => c.count)));
  const dayParts = ["Night", "Morning", "Afternoon", "Evening"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b3a31] via-[#0e5c4e] to-[#06241e] text-white" data-testid="showcase-page">
      {!embed && (
        <header className="border-b border-white/10 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-white" data-testid="showcase-home-link">
              <RudrasLogo size="md" ringed className="ring-white/20" />
              <span className="font-display text-lg font-semibold">Rudras-AI</span>
            </Link>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={replayWalkthrough} className="border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="showcase-replay-btn">
                <Play className="w-3.5 h-3.5 mr-1.5"/> <span className="hidden sm:inline">Replay walkthrough</span><span className="sm:hidden">Replay</span>
              </Button>
              <a href={chatbot_url} target="_blank" rel="noreferrer">
                <Button size="sm" className="bg-emerald-400 text-emerald-950 hover:bg-emerald-300" data-testid="showcase-try-chat">
                  <Bot className="w-3.5 h-3.5 mr-1.5"/> Try Sara
                </Button>
              </a>
            </div>
          </div>
        </header>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-400/15 border border-emerald-300/40 rounded-full text-emerald-200 text-xs uppercase tracking-[0.3em] mb-5" data-testid="showcase-hero-pill">
            <Sparkles className="w-3.5 h-3.5"/> Live Demo · {company?.industry}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold leading-tight max-w-3xl mx-auto" data-testid="showcase-hero-title">
            {company?.name}
          </h1>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto text-base sm:text-lg px-2">
            Explore a fully-seeded AI Sales Assistant in action — lead funnel, engagement scoring, chat activity, and grounded answers.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <Button size="lg" onClick={replayWalkthrough} className="bg-emerald-400 text-emerald-950 hover:bg-emerald-300 font-medium" data-testid="showcase-watch-btn">
              <Play className="w-4 h-4 mr-2"/> Watch the 2-minute walkthrough
            </Button>
            <a href={chatbot_url} target="_blank" rel="noreferrer">
              <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white" data-testid="showcase-chat-btn">
                <Bot className="w-4 h-4 mr-2"/> Talk to Sara
              </Button>
            </a>
          </div>
        </div>

        {/* Executive summary */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="showcase-exec-summary">
          {executive_summary.map((b) => {
            const Icon = ICONS[b.label] || Sparkles;
            return (
              <Card key={b.label} className="p-4 bg-white/[0.06] border-white/10 backdrop-blur hover:bg-white/[0.10] transition">
                <div className="flex items-center gap-2 text-white/60 text-[11px] uppercase tracking-wider">
                  <Icon className="w-3.5 h-3.5"/>{b.label}
                </div>
                <div className="font-display text-2xl sm:text-3xl font-semibold mt-1 tabular-nums text-white">{b.value}</div>
                <div className="text-xs text-white/55 mt-1">{b.sub}</div>
              </Card>
            );
          })}
        </div>

        {/* Funnel + engagement */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <Card className="lg:col-span-2 p-5 sm:p-6 bg-white/[0.06] border-white/10 backdrop-blur" data-testid="showcase-lead-funnel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-white">Lead funnel</h2>
              <span className="text-xs text-white/55">{lead_funnel_total} prospects</span>
            </div>
            <div className="space-y-4">
              {lead_funnel.map((row) => <FunnelBar key={row.status} row={row} max={maxLead}/>)}
            </div>
          </Card>
          <Card className="p-5 sm:p-6 bg-white/[0.06] border-white/10 backdrop-blur" data-testid="showcase-ai-engagement">
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-white">AI engagement</h2>
            <p className="text-xs text-white/55 mb-2">Composite score · depth + capture + volume</p>
            <ScoreGauge score={ai_engagement.score} grade={ai_engagement.grade}/>
            <div className="grid grid-cols-3 gap-2 mt-2 text-center text-white">
              <div><div className="font-semibold tabular-nums">{ai_engagement.total_conversations}</div><div className="text-[11px] text-white/60">Convos</div></div>
              <div><div className="font-semibold tabular-nums">{ai_engagement.avg_turns}</div><div className="text-[11px] text-white/60">Avg turns</div></div>
              <div><div className="font-semibold tabular-nums">{ai_engagement.capture_rate_pct}%</div><div className="text-[11px] text-white/60">Capture</div></div>
            </div>
          </Card>
        </div>

        {/* Heatmap */}
        <Card className="p-5 sm:p-6 bg-white/[0.06] border-white/10 backdrop-blur mb-10" data-testid="showcase-heatmap">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-white">Chat activity</h2>
              <p className="text-xs text-white/55">Last 7 days · by time of day</p>
            </div>
            <Calendar className="w-5 h-5 text-white/40"/>
          </div>
          <div className="grid grid-cols-[60px_1fr] sm:grid-cols-[80px_1fr] gap-2 items-center">
            <div></div>
            <div className="grid grid-cols-4 gap-2 text-[10px] sm:text-[11px] text-white/55 text-center">
              {dayParts.map((p) => <div key={p}>{p}</div>)}
            </div>
            {heatmap.map((row) => (
              <React.Fragment key={row.date}>
                <div className="text-xs text-white/55">
                  <div className="font-medium text-white">{row.day}</div>
                  <div className="text-[10px]">{row.date.slice(5)}</div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {row.cells.map((c, i) => <HeatCell key={i} count={c.count} max={maxHeat}/>)}
                </div>
              </React.Fragment>
            ))}
          </div>
        </Card>

        {/* Inline consultative lead capture */}
        <div className="mb-8" data-testid="showcase-marketing-cta">
          <ShowcaseInboundLeadForm
            slug={slug}
            source={inboundSource}
            chatbotUrl={chatbot_url}
          />
        </div>

        {!embed && (
          <div className="text-center mt-12 text-white/40 text-xs">
            Powered by <Link to="/" className="text-emerald-300 hover:text-emerald-200" data-testid="showcase-footer-home">Rudras-AI</Link> · <Link to="/" className="hover:text-white/60">Marketing site</Link>
          </div>
        )}
      </main>

      <CinematicWalkthrough
        open={walkthroughOpen}
        onClose={() => { setWalkthroughOpen(false); setMode("interactive"); }}
        showcase={data}
        autoStart={true}
        marketingCta={true}
        onSkipToInteractive={handleSkipToInteractive}
        onMarketingCta={() => {
          setWalkthroughOpen(false);
          setMode("interactive");
          setTimeout(() => {
            const el = document.querySelector('[data-testid="showcase-marketing-cta"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 300);
        }}
        embed={embed}
      />
    </div>
  );
}
